/**
 * Path utility functions for internationalization
 */
import { LOCALIZED_PATHS } from "@/constants/pathnames";

/**
 * Convert a path to localized version based on locale
 * @param {string} path - The base path to localize
 * @param {string} locale - The target locale (tr, en, de, ar)
 * @returns {string} - The localized path
 */
export function getLocalizedPath(path, locale = 'tr') {
  // Find the matching path in LOCALIZED_PATHS
  for (const [basePath, localizedPaths] of Object.entries(LOCALIZED_PATHS)) {
    if (typeof localizedPaths === 'string') {
      // Direct path mapping
      if (path === basePath) {
        return localizedPaths;
      }
    } else if (typeof localizedPaths === 'object' && localizedPaths[locale]) {
      // Localized path mapping
      if (path === basePath) {
        return localizedPaths[locale];
      }
    }
  }
  
  // If no mapping found, return original path
  return path;
}

/**
 * Get all available localized paths for a given base path
 * @param {string} path - The base path
 * @returns {object} - Object with locale as key and localized path as value
 */
export function getAllLocalizedPaths(path) {
  const result = {};
  
  for (const [basePath, localizedPaths] of Object.entries(LOCALIZED_PATHS)) {
    if (path === basePath) {
      if (typeof localizedPaths === 'string') {
        // Direct path mapping - return for all locales
        return {
          tr: localizedPaths,
          en: localizedPaths,
          de: localizedPaths,
          ar: localizedPaths
        };
      } else if (typeof localizedPaths === 'object') {
        // Localized path mapping
        return localizedPaths;
      }
    }
  }
  
  // If no mapping found, return original path for all locales
  return {
    tr: path,
    en: path,
    de: path,
    ar: path
  };
}

/**
 * Check if a path has localized versions
 * @param {string} path - The base path to check
 * @returns {boolean} - True if the path has localized versions
 */
export function hasLocalizedPaths(path) {
  return LOCALIZED_PATHS.hasOwnProperty(path);
}
